// Karta komentarzy do pozycji oferty (perspektywa klienta).

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';

import {
  getSalesCrmMyOfferPositionCommentsQueryKey,
  useGetSalesCrmMyOfferPositionComments,
  usePostSalesCrmMyOfferPositionComment
} from 'api';

interface IProps {
  offerId: string;
  positionId: number;
}

const formatDate = (raw: string | null): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ClientPositionCommentsCard: FC<IProps> = ({ offerId, positionId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetSalesCrmMyOfferPositionComments(offerId, positionId);

  const { mutate: addComment, isLoading: isPosting } = usePostSalesCrmMyOfferPositionComment(
    offerId,
    positionId,
    {
      onSuccess: () => {
        setNewComment('');
        setErrorMessage(null);
        queryClient.invalidateQueries(
          getSalesCrmMyOfferPositionCommentsQueryKey(offerId, positionId)
        );
      },
      onError: (e) => setErrorMessage(e?.message ?? t('Nie udało się dodać komentarza.'))
    }
  );

  const handleSubmit = () => {
    const trimmed = newComment.trim();
    if (trimmed === '') {
      setErrorMessage(t('Wprowadź treść komentarza.'));
      return;
    }
    addComment({ comment: trimmed });
  };

  const items = data?.items ?? [];

  return (
    <div
      style={{
        border: '1px solid #eef0f4',
        borderRadius: 6,
        padding: 16,
        background: '#fafbfc'
      }}>
      <div style={{ fontWeight: 600, marginBottom: 12 }}>{t('Komentarze i załączniki')}</div>

      {isLoading && <CircularProgress size={18} />}

      {!isLoading && isError && (
        <div style={{ color: '#b3142d' }}>{t('Nie udało się pobrać komentarzy.')}</div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div style={{ color: '#4b556a', marginBottom: 12 }}>{t('Brak komentarzy.')}</div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {items.map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid #eef0f4',
                borderRadius: 6,
                padding: 10,
                background: c.authorType === 'CLIENT' ? '#f5f3ff' : '#fff'
              }}>
              <div style={{ fontSize: 11, color: '#4b556a', marginBottom: 4 }}>
                {c.authorType === 'CLIENT' ? t('Klient') : t('Handlowiec')}
                {' • '}
                {formatDate(c.createdAt)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{c.comment}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <TextField
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          placeholder={t('Dodaj komentarz...')}
          disabled={isPosting}
        />
        {errorMessage && <div style={{ color: '#b3142d', fontSize: 12 }}>{errorMessage}</div>}
        <div style={{ textAlign: 'right' }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={isPosting}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isPosting ? <CircularProgress size={14} /> : t('Wyślij')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientPositionCommentsCard;
